import React, { useState, useEffect } from "react";
import axios from "axios";

/** Utilities */
const letterFor = (idx) => String.fromCharCode(65 + idx); // 0->A, 1->B...
const range = (n) => Array.from({ length: n }, (_, i) => i + 1); // [1..n]

/** Empty question template */
const emptyQuestion = {
  type: "quiz",
  question: "",
  // Statement type (kept as-is)
  statements: ["", ""],
  // Matching type
  matchingA: ["", ""], // left (A, B, ...)
  matchingB: ["", ""], // right (1, 2, ...)
  // Classic options for quiz/statement
  options: ["", "", "", ""],
  correctAnswer: "",

  // Matching answer controls
  matchingAnswerMode: "single", // "single" | "multiple" | "map"
  matchingCorrectSingle: null, // number (1-based index for the pair)
  matchingCorrectMultiple: [], // number[] (1-based indices of correct pairs)
  matchingMap: {}, // { A: number, B: number, ... } numbers are 1-based right indices

  assertion: "",
  reason: "",
  chronologyItems: ["", "", "", ""],
  explanation: "",
  date: new Date().toISOString().slice(0, 10),
};

const MCQManager = ({
  onUploadSuccess,
  editMCQ,
  onEditClear,
}) => {
  const [questions, setQuestions] = useState([emptyQuestion]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
  if (editMCQ) {
    setQuestions([
      JSON.parse(
        JSON.stringify({
          ...emptyQuestion,
          ...editMCQ,

          matchingCorrectSingle:
            editMCQ.matchingAnswerMode === "single"
              ? editMCQ.correctAnswer?.[0] || null
              : null,

          matchingCorrectMultiple:
            editMCQ.matchingAnswerMode === "multiple"
              ? editMCQ.correctAnswer || []
              : [],

          matchingMap:
            editMCQ.matchingAnswerMode === "map"
              ? editMCQ.matchingMap || {}
              : {},

          date: editMCQ.date
            ? new Date(editMCQ.date).toISOString().slice(0, 10)
            : new Date().toISOString().slice(0, 10),
        })
      ),
    ]);
  }
}, [editMCQ]);

  const token = JSON.parse(localStorage.getItem("user"))?.token;
  const API_BASE =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  /** Generic field updates */
  const updateQuestionField = (index, field, value) => {
  const newQuestions = [...questions];

  // ✅ RESET irrelevant fields when type changes
  if (field === "type") {
    newQuestions[index] = {
      ...emptyQuestion,
      type: value,
      date: newQuestions[index].date,
    };

    setQuestions(newQuestions);
    return;
  }

  newQuestions[index][field] = value;
  setQuestions(newQuestions);
};

  const updateQuestionArrayField = (qIndex, arrayField, idx, value) => {
    const newQuestions = [...questions];
    const arr = [...newQuestions[qIndex][arrayField]];
    arr[idx] = value;
    newQuestions[qIndex][arrayField] = arr;
    setQuestions(newQuestions);
  };

  /** Statement controls */
  const addStatement = (qIndex) => {
    const newQuestions = [...questions];
    if (newQuestions[qIndex].statements.length < 20) {
      newQuestions[qIndex].statements.push("");
      setQuestions(newQuestions);
    }
  };

  const removeStatement = (qIndex, idx) => {
    const newQuestions = [...questions];
    if (newQuestions[qIndex].statements.length > 2) {
      newQuestions[qIndex].statements.splice(idx, 1);
      setQuestions(newQuestions);
    }
  };

  /** Matching pair controls */
  const addMatchingPair = (qIndex) => {
    const newQuestions = [...questions];
    if (newQuestions[qIndex].matchingA.length < 10) {
      newQuestions[qIndex].matchingA.push("");
      newQuestions[qIndex].matchingB.push("");
      setQuestions(newQuestions);
    }
  };

  const removeMatchingPair = (qIndex, idx) => {
    const newQuestions = [...questions];
    if (newQuestions[qIndex].matchingA.length > 2) {
      newQuestions[qIndex].matchingA.splice(idx, 1);
      newQuestions[qIndex].matchingB.splice(idx, 1);
      // Also clean related matching answers if needed
      const leftLabel = letterFor(idx);
      const mode = newQuestions[qIndex].matchingAnswerMode;

      if (mode === "single") {
        // If removing a pair before the selected one, shift indices down
        const sel = newQuestions[qIndex].matchingCorrectSingle;
        if (sel === idx + 1) newQuestions[qIndex].matchingCorrectSingle = null;
        else if (sel > idx + 1) newQuestions[qIndex].matchingCorrectSingle = sel - 1;
      } else if (mode === "multiple") {
        newQuestions[qIndex].matchingCorrectMultiple = newQuestions[qIndex]
          .matchingCorrectMultiple
          .filter((v) => v !== idx + 1)
          .map((v) => (v > idx + 1 ? v - 1 : v));
      } else if (mode === "map") {
        // Delete this left label and shift labels after it
        const currentMap = { ...newQuestions[qIndex].matchingMap };
        delete currentMap[leftLabel];
        // Rebuild map with new left labels (A..)
        const rebuilt = {};
        Object.entries(currentMap).forEach(([k, v]) => {
          const oldIdx = k.charCodeAt(0) - 65; // A->0
          const newIdx = oldIdx > idx ? oldIdx - 1 : oldIdx;
          if (newIdx >= 0) rebuilt[letterFor(newIdx)] = Math.min(
            v > idx + 1 ? v - 1 : v,
            newQuestions[qIndex].matchingB.length // clamp
          );
        });
        newQuestions[qIndex].matchingMap = rebuilt;
      }

      setQuestions(newQuestions);
    }
  };

  /** Question list controls */
  const addNewQuestion = () => {
  setQuestions((prev) => [
    ...prev,
    JSON.parse(JSON.stringify(emptyQuestion)),
  ]);
};

  const removeQuestion = (index) => {
    if (questions.length === 1) return;
    const newQuestions = questions.filter((_, idx) => idx !== index);
    setQuestions(newQuestions);
  };

  /** Validation + payload shaping for matching */
  const shapeMatchingAnswerForPayload = (q) => {
    const pairsCount = q.matchingA.length;

    if (pairsCount !== q.matchingB.length || pairsCount < 2) {
      throw new Error("Matching must have at least 2 valid pairs.");
    }

    if (q.matchingAnswerMode === "single") {
      if (
        typeof q.matchingCorrectSingle !== "number" ||
        q.matchingCorrectSingle < 1 ||
        q.matchingCorrectSingle > pairsCount
      ) {
        throw new Error("Select one correct pair for Matching (single).");
      }
      return {
        correctAnswer: [q.matchingCorrectSingle], // indices
        matchingMap: undefined,
      };
    }

    if (q.matchingAnswerMode === "multiple") {
      if (
        !Array.isArray(q.matchingCorrectMultiple) ||
        q.matchingCorrectMultiple.length === 0
      ) {
        throw new Error("Select at least one correct pair for Matching (multiple).");
      }
      const uniq = [...new Set(q.matchingCorrectMultiple)].filter(
        (n) => n >= 1 && n <= pairsCount
      );
      if (uniq.length === 0) {
        throw new Error("Invalid selection in Matching (multiple).");
      }
      return {
        correctAnswer: uniq, // indices
        matchingMap: undefined,
      };
    }

    if (q.matchingAnswerMode === "map") {
      // Build a clean map with current left labels A.. and right 1..N
      const leftLabels = q.matchingA.map((_, i) => letterFor(i));
      const rightIndices = range(q.matchingB.length);
      const map = {};
      for (const L of leftLabels) {
        const v = q.matchingMap?.[L];
        if (typeof v !== "number" || !rightIndices.includes(v)) {
          throw new Error(`Provide mapping for ${L} in Matching (map).`);
        }
        map[L] = v; // 1-based
      }
      return {
        correctAnswer: undefined, // not used in map mode
        matchingMap: map,
      };
    }

    throw new Error("Unknown matching answer mode.");
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      // Prepare payload: transform each question as needed
      const prepared = questions.map((q) => {
        if (q.type !== "matching") return q;
        if (q.type === "assertion-reason") {
  return {
    ...q,
    options: [
      "Both A and R are true, and R is the correct explanation of A",
      "Both A and R are true, but R is not the correct explanation of A",
      "A is true, but R is false",
      "A is false, but R is true",
    ],
  };
}

        const shaped = shapeMatchingAnswerForPayload(q);

        const toSend = {
          ...q,
          // Ensure indices are sent (Q2 = A)
          correctAnswer:
            q.matchingAnswerMode === "map" ? undefined : shaped.correctAnswer,
          matchingMap:
            q.matchingAnswerMode === "map" ? shaped.matchingMap : undefined,
        };

        // Optional: send a compact human-readable for reference
        if (q.matchingAnswerMode === "single") {
          toSend.correctAnswerReadable = `Only (${letterFor(
            (shaped.correctAnswer?.[0] ?? 1) - 1
          ).toLowerCase()})`;
        } else if (q.matchingAnswerMode === "multiple") {
          const list = (shaped.correctAnswer || [])
            .map((n) => letterFor(n - 1).toLowerCase())
            .join(", ");
          toSend.correctAnswerReadable = `(${list}) are correct`;
        } else if (q.matchingAnswerMode === "map") {
          const seq = Object.entries(shaped.matchingMap || {})
            .map(([k, v]) => `${k}-${v}`)
            .join(", ");
          toSend.correctAnswerReadable = seq; // e.g., A-3, B-1, C-4...
        }

        return toSend;
      });

      const payload = { mcqs: prepared };

      let res;

if (editMCQ?._id) {
  // ✅ EDIT MODE
  const updatedQuestion = prepared[0];

  res = await axios.put(
    `${API_BASE}/api/admin/mcqs/${editMCQ._id}`,
    updatedQuestion,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  setMessage("MCQ updated successfully");

  onEditClear && onEditClear();

} else {
  // ✅ BULK UPLOAD MODE
  res = await axios.post(
    `${API_BASE}/api/admin/mcqs/bulk`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  setMessage(res.data.message || "Bulk upload successful");
}
      setQuestions([emptyQuestion]);
      onUploadSuccess && onUploadSuccess();
    } catch (error) {
      setMessage(error.response?.data?.error || error.message || "Bulk upload failed");
    } finally {
      setLoading(false);
    }
  };
  console.log("editMCQ", editMCQ);

  /** Render */
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">

  <div>
    <h2 className="text-4xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
      {editMCQ ? "Edit MCQ" : "MCQ Management"}
    </h2>

    <p className="text-gray-500 dark:text-gray-400 mt-2">
      Create modern UPSC MCQs with advanced formats.
    </p>
  </div>

  <div className="flex items-center gap-3">

    <div className="px-4 py-2 rounded-2xl bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 font-semibold shadow-sm">
      {questions.length} Question{questions.length > 1 ? "s" : ""}
    </div>

    {editMCQ && (
      <div className="px-4 py-2 rounded-2xl bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300 font-semibold shadow-sm">
        Editing Mode
      </div>
    )}

  </div>

</div>

      <form onSubmit={handleBulkUpload} className="space-y-8">
        {questions.map((q, qIndex) => {
          const pairCount = q.matchingA.length;
          const leftLabels = q.matchingA.map((_, i) => letterFor(i)); // A,B,C...
          const rightIndices = range(q.matchingB.length); // 1..N

          return (
            <div
  key={editMCQ?._id || qIndex}
              className="
group
relative
overflow-hidden
rounded-3xl
border
border-gray-200
dark:border-gray-700
bg-white/90
dark:bg-gray-800/90
backdrop-blur-xl
shadow-lg
hover:shadow-2xl
transition-all
duration-300
p-8
space-y-6
"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

  <div className="flex items-center gap-4">

    <div className="
      h-14
      w-14
      rounded-2xl
      bg-gradient-to-br
      from-blue-500
      to-purple-600
      text-white
      flex
      items-center
      justify-center
      font-bold
      text-lg
      shadow-lg
    ">
      {qIndex + 1}
    </div>

    <div>

      <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100">
        Question {qIndex + 1}
      </h3>

      <p className="text-sm text-gray-500 dark:text-gray-400">
        {q.type.toUpperCase()}
      </p>

    </div>

  </div>

  {questions.length > 1 && (
    <button
      type="button"
      onClick={() => removeQuestion(qIndex)}
      className="
        px-4 py-2
        rounded-xl
        bg-red-50
        dark:bg-red-900/30
        text-red-600
        dark:text-red-400
        border
        border-red-200
        dark:border-red-700
        hover:scale-105
        transition
      "
    >
      Remove Question
    </button>
  )}

</div>

              {/* Type */}
              <div>
                <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
                  Type
                </label>
                <select
                  name="type"
                  value={q.type}
                  onChange={(e) => updateQuestionField(qIndex, "type", e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="quiz">Quiz</option>
                  <option value="statement">Statement</option>
                  <option value="matching">Matching</option>
                  <option value="assertion-reason">Assertion-Reason</option>
                  <option value="chronology">Chronology</option>
                </select>
              </div>

              {/* Question */}
              <div>
                <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
                  Question
                </label>
               <textarea
name="question"

value={q.question}

onChange={(e)=>

updateQuestionField(

qIndex,

"question",

e.target.value

)

}

placeholder={`
Example:

Consider the following assertion:

Statement here...

Which of the following is correct?

Select the answer using code below:
`}

rows={8}

className="
w-full
min-h-[220px]
border
border-gray-300
dark:border-gray-600
rounded-2xl
px-5
py-4
shadow-sm
focus:ring-2
focus:ring-blue-500
focus:border-blue-500
transition-all
resize-y
whitespace-pre-wrap
dark:bg-gray-800
dark:text-white
"

required
/>
              </div>

              {/* Statement type */}
              {q.type === "statement" && (
                <div>
                  <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
                    Statements
                  </label>
                  {q.statements.map((stmt, idx) => (
                    <div key={idx} className="flex space-x-2 items-center mb-2">
                      <textarea
                        value={stmt}
                        onChange={(e) =>
                          updateQuestionArrayField(
                            qIndex,
                            "statements",
                            idx,
                            e.target.value
                          )
                        }
                        className="flex-grow border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder={`Statement ${idx + 1}`}
                        required
                      />
                      {q.statements.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeStatement(qIndex, idx)}
                          className="text-red-600 dark:text-red-400 font-bold text-xl"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  ))}
                  {q.statements.length < 20 && (
                    <button
                      type="button"
                      onClick={() => addStatement(qIndex)}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded"
                    >
                      + Add Statement
                    </button>
                  )}
                </div>
              )}

              {/* Options for quiz/statement */}
              {(q.type === "quiz" || q.type === "statement") && (
                <div>
                  <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
                    Options
                  </label>
                  {q.options.map((opt, idx) => (
                    <input
                      key={idx}
                      type="text"
                      value={opt}
                      onChange={(e) =>
                        updateQuestionArrayField(
                          qIndex,
                          "options",
                          idx,
                          e.target.value
                        )
                      }
                      placeholder={`Option ${idx + 1}`}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      required
                    />
                  ))}
                  <div className="mt-2">
                    <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
                      Correct Answer
                    </label>
                    <input
                      type="text"
                      value={q.correctAnswer}
                      onChange={(e) =>
                        updateQuestionField(qIndex, "correctAnswer", e.target.value)
                      }
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Matching */}
              {q.type === "matching" && (
                <div className="space-y-4">
                  <div>
                    <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
                      Matching Pairs
                    </label>
                    {q.matchingA.map((_, idx) => (
                      <div key={idx} className="flex space-x-2 items-center mb-2">
                        <input
                          type="text"
                          value={q.matchingA[idx]}
                          onChange={(e) =>
                            updateQuestionArrayField(
                              qIndex,
                              "matchingA",
                              idx,
                              e.target.value
                            )
                          }
                          placeholder={`Left ${letterFor(idx)} (${idx + 1})`}
                          className="flex-grow border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          required
                        />
                        <input
                          type="text"
                          value={q.matchingB[idx]}
                          onChange={(e) =>
                            updateQuestionArrayField(
                              qIndex,
                              "matchingB",
                              idx,
                              e.target.value
                            )
                          }
                          placeholder={`Right ${idx + 1}`}
                          className="flex-grow border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          required
                        />
                        {q.matchingA.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeMatchingPair(qIndex, idx)}
                            className="text-red-600 dark:text-red-400 font-bold text-xl"
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    ))}
                    {q.matchingA.length < 10 && (
                      <button
                        type="button"
                        onClick={() => addMatchingPair(qIndex)}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded"
                      >
                        + Add Pair
                      </button>
                    )}
                  </div>

                  {/* Matching: Answer Mode */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <label className="block font-medium mb-2 text-gray-700 dark:text-gray-200">
                      Correct Answer Type (Matching)
                    </label>
                    <select
                      value={q.matchingAnswerMode}
                      onChange={(e) =>
                        updateQuestionField(qIndex, "matchingAnswerMode", e.target.value)
                      }
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="single">Single correct pair (e.g., only (c))</option>
                      <option value="multiple">Multiple correct pairs (e.g., (a) & (c))</option>
                      <option value="map">Full matching map (A→3, B→1...)</option>
                    </select>

                    {/* Mode: Single */}
                    {q.matchingAnswerMode === "single" && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {leftLabels.map((L, idx) => (
                          <label
                            key={L}
                            className="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700"
                          >
                            <input
                              type="radio"
                              name={`single-${qIndex}`}
                              checked={q.matchingCorrectSingle === idx + 1}
                              onChange={() =>
                                updateQuestionField(
                                  qIndex,
                                  "matchingCorrectSingle",
                                  idx + 1
                                )
                              }
                            />
                            <span className="text-sm text-gray-800 dark:text-gray-100">
                              ({L.toLowerCase()}) {q.matchingA[idx]} — {q.matchingB[idx]}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Mode: Multiple */}
                    {q.matchingAnswerMode === "multiple" && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {leftLabels.map((L, idx) => {
                          const isChecked = q.matchingCorrectMultiple?.includes(idx + 1);
                          return (
                            <label
                              key={L}
                              className="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700"
                            >
                              <input
                                type="checkbox"
                                checked={!!isChecked}
                                onChange={(e) => {
                                  const next = new Set(q.matchingCorrectMultiple || []);
                                  if (e.target.checked) next.add(idx + 1);
                                  else next.delete(idx + 1);
                                  updateQuestionField(
                                    qIndex,
                                    "matchingCorrectMultiple",
                                    Array.from(next).sort((a, b) => a - b)
                                  );
                                }}
                              />
                              <span className="text-sm text-gray-800 dark:text-gray-100">
                                ({L.toLowerCase()}) {q.matchingA[idx]} — {q.matchingB[idx]}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {/* Mode: Map */}
                    {q.matchingAnswerMode === "map" && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {leftLabels.map((L, idx) => (
                          <div key={L} className="flex items-center space-x-3">
                            <span className="w-12 text-gray-700 dark:text-gray-200 font-medium">
                              {L} →
                            </span>
                            <select
                              value={q.matchingMap?.[L] ?? ""}
                              onChange={(e) => {
                                const val = e.target.value ? parseInt(e.target.value, 10) : null;
                                const next = { ...(q.matchingMap || {}) };
                                if (val == null) delete next[L];
                                else next[L] = val;
                                updateQuestionField(qIndex, "matchingMap", next);
                              }}
                              className="flex-1 border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            >
                              <option value="">Select</option>
                              {rightIndices.map((n) => (
                                <option key={n} value={n}>
                                  {n} ({q.matchingB[n - 1] || `Right ${n}`})
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Assertion Reason */}
{q.type === "assertion-reason" && (
  <div className="space-y-4">

    {/* Assertion */}
    <div>
      <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
        Assertion (A)
      </label>

      <textarea
        value={q.assertion}
        onChange={(e) =>
          updateQuestionField(qIndex, "assertion", e.target.value)
        }
        className="w-full border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
        rows={3}
        required
      />
    </div>

    {/* Reason */}
    <div>
      <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
        Reason (R)
      </label>

      <textarea
        value={q.reason}
        onChange={(e) =>
          updateQuestionField(qIndex, "reason", e.target.value)
        }
        className="w-full border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
        rows={3}
        required
      />
    </div>

    {/* Correct Answer */}
    <div>
      <label className="block font-medium mb-2 text-gray-700 dark:text-gray-200">
        Correct Answer
      </label>

      <select
        value={q.correctAnswer}
        onChange={(e) =>
          updateQuestionField(qIndex, "correctAnswer", e.target.value)
        }
        className="w-full border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
        required
      >
        <option value="">Select Correct Option</option>

        <option value="Both A and R are true, and R is the correct explanation of A">
          (a) Both A and R are true, and R is the correct explanation of A
        </option>

        <option value="Both A and R are true, but R is not the correct explanation of A">
          (b) Both A and R are true, but R is not the correct explanation of A
        </option>

        <option value="A is true, but R is false">
          (c) A is true, but R is false
        </option>

        <option value="A is false, but R is true">
          (d) A is false, but R is true
        </option>
      </select>
    </div>

  </div>
)}

              {/* Explanation */}
              <div>
                <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
                  Explanation
                </label>
                <textarea
                  value={q.explanation}
                  onChange={(e) =>
                    updateQuestionField(qIndex, "explanation", e.target.value)
                  }
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  rows={3}
                />
              </div>

              {/* Date */}
              <div>
                <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
                  Date
                </label>
                <input
                  type="date"
                  value={q.date}
                  onChange={(e) => updateQuestionField(qIndex, "date", e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  required
                />
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={addNewQuestion}
          className="
bg-gradient-to-r
from-blue-600
to-purple-600
hover:from-blue-700
hover:to-purple-700
text-white
px-6
py-3
rounded-2xl
shadow-lg
hover:shadow-xl
transition-all
duration-300
hover:scale-[1.02]
font-semibold
"
        >
          + Add New Question
        </button>

        <button
          type="submit"
          disabled={loading}
          className="
w-full
mt-6
bg-gradient-to-r
from-green-600
to-emerald-600
hover:from-green-700
hover:to-emerald-700
text-white
py-4
rounded-2xl
shadow-xl
hover:shadow-2xl
transition-all
duration-300
font-bold
text-lg
tracking-wide
disabled:opacity-50
disabled:cursor-not-allowed
"
        >
          {loading
  ? editMCQ
    ? "Updating..."
    : "Uploading..."
  : editMCQ
  ? "Update MCQ"
  : "Upload All Questions"}
        </button>

        {message && (
          <div
  className={`
    mt-5
    p-4
    rounded-2xl
    border
    text-center
    font-semibold
    shadow-sm
    ${
      message.toLowerCase().includes("error") ||
      message.toLowerCase().includes("fail")
        ? "bg-red-50 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300"
        : "bg-green-50 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300"
    }
  `}
>
  {message}
</div>
        )}
      </form>
    </div>
  );
};

export default MCQManager;
